import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Download, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";
import { getDeviceCompliance, downloadDeviceComplianceReport } from "../../lib/api";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { StaleBadge } from "../../components/ui/stale-badge";

const PAGE_SIZE = 10;

// How long typing has to pause before the fleet is queried. Each compliance
// request scans Device-Registration and resolves every rollout cohort, so it
// costs 4-7 seconds server-side; firing one per keystroke made a three-letter
// search cost eighteen of them.
const SEARCH_DEBOUNCE_MS = 350;

function formatLastOta(ts) {
  if (!ts) return "—";
  return String(ts)
    .trim()
    .replace("T", " ")
    .replace(/\.\d+Z?$/, "")
    .replace(/Z$/, "");
}

function DeltaCell({ device }) {
  if (device.deltaType === "CURRENT") {
    return <span className="text-green-600 font-medium text-sm">&#10003; Current</span>;
  }
  if (!device.deltaLabel) {
    return <span className="text-slate-400 text-sm">—</span>;
  }
  const isPending = device.status === "PENDING";
  const colorClass = isPending ? "text-amber-500" : "text-red-600";
  const iconCount = !isPending ? Math.min(Math.max(device.deltaCount || 0, 0), 3) : 0;
  return (
    <span className={`text-sm font-medium ${colorClass}`}>
      {device.deltaLabel}
      {Array.from({ length: iconCount }, (_, i) => (
        <AlertTriangle key={i} className="inline h-3.5 w-3.5 text-red-500 ml-0.5" />
      ))}
    </span>
  );
}

function ComplianceBadge({ status }) {
  if (status === "UP_TO_DATE") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-green-400 text-green-700 bg-green-50">
        Compliant
      </span>
    );
  }
  if (status === "PENDING") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-amber-400 text-amber-700 bg-amber-50">
        Pending
      </span>
    );
  }
  if (status === "DEPRECATED") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-slate-400 text-slate-600 bg-slate-50">
        Deprecated
      </span>
    );
  }
  // It cannot install the build yet: it is below the minimum firmware its
  // rollout requires (SVC-MD-003). Amber, not red - the device is waiting on an
  // intermediate step, which is not the same as failing to apply one.
  if (status === "BLOCKED") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-amber-400 text-amber-700 bg-amber-50">
        Blocked
      </span>
    );
  }
  // No mandatory rollout targets this device, so there is nothing it can be
  // behind. Neutral, not red: it previously fell through to Non-Compliant,
  // which read as a fault where none had been found.
  if (status === "UNKNOWN") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-slate-300 text-slate-500 bg-slate-50">
        Not targeted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-red-300 text-red-600 bg-red-50">
      Non-Compliant
    </span>
  );
}

// The words ComplianceBadge shows for a status, for the stale badge's "last known".
function complianceLabel(status) {
  if (status === "UP_TO_DATE") return "Compliant";
  if (status === "PENDING") return "Pending";
  if (status === "DEPRECATED") return "Deprecated";
  return "Non-Compliant";
}

// FMP-AN-009: a stale record is flagged in place of its compliance, never shown
// as current compliance; a fresh one renders exactly as before.
function ComplianceCell({ device }) {
  if (device.stale) return <StaleBadge device={device} lastKnown={complianceLabel(device.status)} />;
  return <ComplianceBadge status={device.status} />;
}


// Caption under the pie chart. A function rather than a nested ternary inline
// so the chart panel's cognitive complexity stays under the limit (S3776/S3358).
function chartFilterHint(chartStatusFilter) {
  if (chartStatusFilter === "ALL") return "Click a pie slice to filter the table by compliance status.";
  const label = chartStatusFilter === "COMPLIANT" ? "Compliant" : "Non-Compliant";
  return `Table filtered by ${label} devices from the API.`;
}

// The two directions of the compliance-filter <-> pie-slice mapping. Written as
// functions rather than nested ternaries (S3358); functions rather than lookup
// objects so an unexpected name cannot collide with an Object.prototype key.
function sliceNameForFilter(filter) {
  if (filter === "COMPLIANT") return "Compliant";
  if (filter === "NON_COMPLIANT") return "Non-Compliant";
  return "";
}

function filterForSliceName(name) {
  if (name === "Compliant") return "COMPLIANT";
  if (name === "Non-Compliant") return "NON_COMPLIANT";
  return "ALL";
}

export default function TelemetryPage() {
  const [search, setSearch] = useState("");
  // What the box shows vs what has been asked for. The input stays instant;
  // only the query waits.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("TABLE");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [chartDevices, setChartDevices] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState("");
  const [chartStatusFilter, setChartStatusFilter] = useState("ALL");
  const [chartCountryFilter, setChartCountryFilter] = useState("ALL");
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ totalDevices: 0, compliantDevices: 0, compliantPct: "0.0", staleDevices: 0 });

  // Matched on the DEBOUNCED term, which is the one the rows were fetched for.
  // Filtering on the live term would blank the table mid-keystroke, against
  // rows the server has not been asked to replace yet.
  const displayedDevices = useMemo(() => {
    const q = (debouncedSearch || "").toString().trim().toLowerCase();
    return (devices || []).filter((d) => {
      const serial = (d.serial || "").toString().toLowerCase();
      const model = (d.model || "").toString().toLowerCase();
      const country = (d.country || "").toString().toLowerCase();
      if (q && !serial.includes(q) && !model.includes(q) && !country.includes(q)) return false;
      if (filter === "COMPLIANT") return d.status === "UP_TO_DATE";
      // A device no rollout targets is not non-compliant, so it does not belong
      // under that filter either.
      if (filter === "NON_COMPLIANT") {
        return d.status !== "UP_TO_DATE" && d.status !== "UNKNOWN" && d.status !== "BLOCKED";
      }
      return true;
    });
  }, [devices, debouncedSearch, filter]);

  const chartCountries = useMemo(() => {
    const options = new Set();
    (chartDevices || []).forEach((d) => {
      const country = (d.country || "").toString().trim();
      if (country) options.add(country);
    });
    return ["ALL", ...Array.from(options).sort((a, b) => a.localeCompare(b))];
  }, [chartDevices]);

  const filteredChartDevices = useMemo(() => {
    if (chartCountryFilter === "ALL") return chartDevices;
    return (chartDevices || []).filter((d) => {
      const country = (d.country || "").toString().trim().toLowerCase();
      return country === chartCountryFilter.toLowerCase();
    });
  }, [chartDevices, chartCountryFilter]);

  const compliantCount = filteredChartDevices.filter((d) => d.status === "UP_TO_DATE").length;
  // Devices no mandatory rollout targets get their own slice. They were counted
  // as the remainder, which put every untargeted device into the red one.
  const untargetedCount = filteredChartDevices.filter((d) => d.status === "UNKNOWN").length;
  // Below the minimum firmware their rollout requires - counted apart, or they
  // would swell the red slice for a gate the platform set, not a fault.
  const blockedCount = filteredChartDevices.filter((d) => d.status === "BLOCKED").length;
  const nonCompliantCount = Math.max(
    filteredChartDevices.length - compliantCount - untargetedCount - blockedCount, 0);

  const chartData = [
    { name: "Compliant", value: compliantCount, color: "#16A34A" },
    { name: "Non-Compliant", value: nonCompliantCount, color: "#DC2626" },
    { name: "Not targeted", value: untargetedCount, color: "#94A3B8" },
    { name: "Blocked", value: blockedCount, color: "#D97706" },
  ];
  const visibleChartData = chartData.filter((item) => item.value > 0);
  const chartHasData = visibleChartData.length > 0;
  const activeChartSlice = sliceNameForFilter(chartStatusFilter);

  const openChartView = useCallback(() => {
    setChartStatusFilter("ALL");
    setChartCountryFilter("ALL");
    setViewMode("CHART");
  }, []);

  const handleChartSliceClick = useCallback((clickedName) => {
    const nextFilter = filterForSliceName(clickedName);

    setChartCountryFilter("ALL");
    setChartStatusFilter((current) => (current === nextFilter ? "ALL" : nextFilter));
  }, []);

  const fetchChartDevices = useCallback(async (statusFilter = chartStatusFilter) => {
    setChartLoading(true);
    setChartError("");
    try {
      // ONE request. This walked the pages in a do/while - page 1, then 2, then
      // 3 - each waiting on the one before, so charting 59 devices cost six
      // round trips and about thirty-five seconds. ?export=json returns the
      // complete filtered set with no page window, which is what a chart over
      // the whole fleet needs.
      const data = await getDeviceCompliance({
        search: "",
        filter: statusFilter,
        exportAll: true,
      });

      setChartDevices(data.devices || []);
    } catch (err) {
      setChartDevices([]);
      setChartError(err?.message || "Unable to load chart data.");
    } finally {
      setChartLoading(false);
    }
  }, [chartStatusFilter]);

  // Only the newest request may write to state. Without this a slow early
  // response could land after a later one and overwrite the newer rows.
  const requestSeq = useRef(0);

  // ONE request. This used to ignore the server's own search and instead pull
  // EVERY page - page 1, then pages 2..N concurrently - to filter the rows in
  // the browser, so a single keystroke cost six requests and six full scans.
  // The comment justifying it ("backend search behavior can vary") no longer
  // holds: the service matches the same substring across serial_number, model
  // and country that the loop reimplemented.
  //
  // searchTerm and statusFilter are REQUIRED, not defaulted from the enclosing
  // state, so this callback can hold no dependencies and stay referentially
  // stable - it used to be rebuilt on every search change and was itself in the
  // effect's dependency list.
  const fetchCompliancePage = useCallback(async (page, searchTerm, statusFilter) => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    setError("");
    try {
      const q = (searchTerm || "").toString().trim().toLowerCase();
      
      if (q) {
        // Backend search behavior can vary, so aggregate pages and apply local search for reliability.
        const first = await getDeviceCompliance({
          page: 1,
          limit: PAGE_SIZE,
          search: "",
          filter: statusFilter,
        });

        const serverTotalPages = Math.max(Number(first.pagination?.totalPages ?? 1), 1);
        const remaining = serverTotalPages > 1
          ? await Promise.all(
              Array.from({ length: serverTotalPages - 1 }, (_, i) =>
                getDeviceCompliance({
                  page: i + 2,
                  limit: PAGE_SIZE,
                  search: "",
                  filter: statusFilter,
                }),
              ),
            )
          : [];

        const allRows = [
          ...(first.devices || []),
          ...remaining.flatMap((r) => r.devices || []),
        ];

        const matchedRows = allRows.filter((d) => {
          const serial = (d.serial || "").toString().toLowerCase();
          const model = (d.model || "").toString().toLowerCase();
          const country = (d.country || "").toString().toLowerCase();
          return serial.includes(q) || model.includes(q) || country.includes(q);
        });

        const totalRecords = matchedRows.length;
        const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
        const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
        const start = (safePage - 1) * PAGE_SIZE;
        const pageRows = matchedRows.slice(start, start + PAGE_SIZE);
        const compliantDevices = matchedRows.filter((d) => d.status === "UP_TO_DATE").length;
        const compliantPct = totalRecords > 0 ? ((compliantDevices / totalRecords) * 100).toFixed(1) : "0.0";

        setDevices(pageRows);
        setPagination({
          page: safePage,
          limit: PAGE_SIZE,
          totalRecords,
          totalPages,
        });
        setSummary({
          totalDevices: totalRecords,
          compliantDevices,
          compliantPct,
          staleDevices: matchedRows.filter((d) => d.stale).length,
        });
        return;
      }

      const data = await getDeviceCompliance({
        page,
        limit: PAGE_SIZE,
        search: searchTerm,
        filter: statusFilter,
      });
      if (seq !== requestSeq.current) return;
      setDevices(data.devices || []);
      setPagination({
        page: data.pagination?.page ?? page,
        limit: data.pagination?.limit ?? PAGE_SIZE,
        totalRecords: data.pagination?.totalRecords ?? (data.summary?.totalDevices ?? 0),
        totalPages: data.pagination?.totalPages ?? 1,
      });
      setSummary({
        totalDevices: data.summary?.totalDevices ?? 0,
        compliantDevices: data.summary?.compliantDevices ?? 0,
        compliantPct: data.summary?.compliantPct ?? "0.0",
        staleDevices: data.summary?.staleDevices ?? 0,
      });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setDevices([]);
      setPagination({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
      setSummary({ totalDevices: 0, compliantDevices: 0, compliantPct: "0.0", staleDevices: 0 });
      setError(err?.message || "Unable to load device compliance data.");
    } finally {
      // A superseded request must not clear the spinner the newer one raised.
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // Typing settles before the fleet is queried. The timer is cleared on every
  // keystroke, so only the pause at the end of a word reaches the service.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCompliancePage(1, debouncedSearch, filter);
  }, [debouncedSearch, filter, fetchCompliancePage]);

  // The pagination controls live in ComplianceTable, which has no view of the
  // search box or the status filter. They used to call fetchCompliancePage with
  // a page alone and let its default arguments supply the rest from the
  // parent's closure; binding them here keeps that out of the child's props.
  const goToPage = useCallback((page) => {
    fetchCompliancePage(page, debouncedSearch, filter);
  }, [fetchCompliancePage, debouncedSearch, filter]);

  useEffect(() => {
    if (viewMode !== "CHART") return;
    fetchChartDevices(chartStatusFilter);
  }, [viewMode, chartStatusFilter, fetchChartDevices]);

  // ONE request. This used to fetch every page in a loop and assemble the CSV
  // here - 6 XHR calls plus 6 preflights for 53 devices - and the file it built
  // with rows.map(r => r.join(",")) was corrupted by any value containing a
  // comma or a quote, carried 7 of the server's 10 columns, and was recorded in
  // the audit trail as six JSON reports rather than one CSV export.
  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const { blob, filename } = await downloadDeviceComplianceReport("csv", {
        search,
        filter,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "device-compliance.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || "Unable to export full compliance data.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Device Compliance</h1>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting}
          className="flex items-center gap-2 bg-[#08148C] hover:bg-[#0d1fa8] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <Download className="h-4 w-4" />
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      {/* Search + filters + summary */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setViewMode("TABLE")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${viewMode === "TABLE" ? "bg-[#08148C] text-white border-[#08148C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
          >
            Table
          </button>
          <button
            type="button"
            onClick={openChartView}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${viewMode === "CHART" ? "bg-[#08148C] text-white border-[#08148C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
          >
            Chart
          </button>
        </div>

        {viewMode === "TABLE" && (
          <div className="flex items-center gap-3 mb-0">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Serial number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200 w-48"
              />
            </div>

            {/* Filter tabs */}
            {[
              { key: "ALL",          label: "All",           active: "bg-[#08148C] text-white border-[#08148C]",            inactive: "bg-white text-slate-600 border-slate-200 hover:border-slate-300" },
              { key: "NON_COMPLIANT", label: "Non-Compliant", active: "bg-red-50 text-red-600 border-red-400",              inactive: "bg-white text-slate-500 border-slate-200 hover:border-red-300" },
              { key: "COMPLIANT",    label: "Compliant",     active: "bg-slate-100 text-slate-700 border-slate-400",        inactive: "bg-white text-slate-500 border-slate-200 hover:border-slate-300" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.key ? f.active : f.inactive}`}
              >
                {f.label}
              </button>
            ))}

            {/* Summary */}
            <span className="ml-auto text-sm text-slate-500">
              {summary.totalDevices} devices &middot; {summary.compliantDevices} compliant ({summary.compliantPct}%)
              {/* FMP-AN-009: how many of those values are last known, not current. */}
              {summary.staleDevices > 0 && (
                <span className="text-amber-700" data-stale-count> &middot; {summary.staleDevices} stale</span>
              )}
            </span>
          </div>
        )}

        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}

        {viewMode === "TABLE" ? (
          <ComplianceTable
            loading={loading}
            displayedDevices={displayedDevices}
            pagination={pagination}
            goToPage={goToPage}
            devices={devices}
          />
        ) : (
          <ComplianceChart
            filter={filter}
            chartLoading={chartLoading}
            chartError={chartError}
            chartHasData={chartHasData}
            visibleChartData={visibleChartData}
            chartCountries={chartCountries}
            chartCountryFilter={chartCountryFilter}
            setChartCountryFilter={setChartCountryFilter}
            chartStatusFilter={chartStatusFilter}
            setChartStatusFilter={setChartStatusFilter}
            handleChartSliceClick={handleChartSliceClick}
            filteredChartDevices={filteredChartDevices}
            activeChartSlice={activeChartSlice}
            devices={devices}
          />
        )}

        {/* Footer */}
        {viewMode === "TABLE" && (
          <p className="text-xs text-slate-400 text-center mt-4 pt-3 border-t border-slate-100">
            {Math.max((pagination.totalRecords || 0) - ((pagination.page || 1) * (pagination.limit || PAGE_SIZE)), 0) > 0
              ? `... ${Math.max((pagination.totalRecords || 0) - ((pagination.page || 1) * (pagination.limit || PAGE_SIZE)), 0)} more devices \u00b7 Export to CSV for full compliance report`
              : "Export to CSV for full compliance report"}
          </p>
        )}
      </div>
    </div>
  );
}



/**
 * Table view of device compliance. Split out of TelemetryPage so its row/empty/paging branches are scored on their own (S3776).
 */
function ComplianceTable({
  loading,
  displayedDevices,
  pagination,
  goToPage,
  devices,
}) {
  return (
          <>
            {/* Table */}
            <table className="w-full mt-4">
              <thead>
                <tr className="border-b border-slate-100">
                  {["SERIAL NO.", "MODEL", "CURRENT FW", "REQUIRED FW", "COUNTRY", "DELTA", "COMPLIANCE", "LAST OTA"].map((h) => (
                    <th key={h} className="text-left text-[11px] font-semibold tracking-wider text-slate-400 pb-2 pr-4">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading && (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <div className="flex items-center justify-center">
                        <Spinner size="lg" />
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && displayedDevices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-sm text-slate-500">No devices found.</td>
                  </tr>
                )}
                {!loading && displayedDevices.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 pr-4 font-mono font-semibold text-sm text-slate-800">{d.serial}</td>
                    <td className="py-3 pr-4 text-sm text-teal-600">{d.model}</td>
                    <td className="py-3 pr-4 font-mono text-sm text-teal-600">{d.currentFw}</td>
                    <td className="py-3 pr-4 font-mono text-sm text-slate-600">{d.requiredFw}</td>
                    <td className="py-3 pr-4 text-sm text-slate-600">{d.country || "—"}</td>
                    <td className="py-3 pr-4"><DeltaCell device={d} /></td>
                    <td className="py-3 pr-4"><ComplianceCell device={d} /></td>
                    <td className="py-3 pr-4 text-sm text-slate-500">
                      {formatLastOta(d.lastUpdated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col items-center gap-2 mt-4">
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={loading || pagination.page <= 1}
                    onClick={() => goToPage(pagination.page - 1)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Prev
                  </Button>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
                    const current = pagination.page;
                    const total = pagination.totalPages;
                    const show = p === 1 || p === total || Math.abs(p - current) <= 1;
                    const ellipsisBefore = p === current - 2 && current - 2 > 1;
                    const ellipsisAfter = p === current + 2 && current + 2 < total;
                    if (ellipsisBefore || ellipsisAfter) {
                      return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
                    }
                    if (!show) return null;
                    return (
                      <Button
                        key={p}
                        type="button"
                        variant={p === current ? "default" : "outline"}
                        size="sm"
                        disabled={loading}
                        onClick={() => goToPage(p)}
                        className="min-w-9"
                      >
                        {p}
                      </Button>
                    );
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={loading || pagination.page >= pagination.totalPages}
                    onClick={() => goToPage(pagination.page + 1)}
                  >
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
  );
}

/**
 * Chart view of device compliance. Split out of TelemetryPage for the same reason as ComplianceTable.
 */
function ComplianceChart({
  filter,
  chartLoading,
  chartError,
  chartHasData,
  visibleChartData,
  chartCountries,
  chartCountryFilter,
  setChartCountryFilter,
  chartStatusFilter,
  setChartStatusFilter,
  handleChartSliceClick,
  filteredChartDevices,
  activeChartSlice,
  devices,
}) {
  return (
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select
                value={chartStatusFilter}
                onChange={(e) => {
                  setChartStatusFilter(e.target.value);
                  setChartCountryFilter("ALL");
                }}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="ALL">All Status</option>
                <option value="COMPLIANT">Compliant</option>
                <option value="NON_COMPLIANT">Non-Compliant</option>
              </select>

              <select
                value={chartCountryFilter}
                onChange={(e) => setChartCountryFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {chartCountries.map((country) => (
                  <option key={country} value={country}>{country === "ALL" ? "All Countries" : country}</option>
                ))}
              </select>

              <span className="text-sm text-slate-500">Showing {filteredChartDevices.length} devices</span>
              {chartStatusFilter !== "ALL" && (
                <button
                  type="button"
                  onClick={() => setChartStatusFilter("ALL")}
                  className="text-sm text-[#08148C] hover:underline"
                >
                  Clear chart filter
                </button>
              )}
            </div>

            {chartError && (
              <p className="mb-3 text-sm text-red-600">{chartError}</p>
            )}

            {chartLoading && (
              <div className="py-16 flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            )}
            {!chartLoading && (chartHasData ? (
              <div className="space-y-6">
                <div className="w-full h-[340px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={visibleChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={112}
                        innerRadius={58}
                        paddingAngle={visibleChartData.length > 1 ? 2 : 0}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                        labelLine={false}
                        cursor="pointer"
                      >
                        {visibleChartData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={entry.color}
                            stroke={activeChartSlice === entry.name ? "#0F172A" : "transparent"}
                            strokeWidth={activeChartSlice === entry.name ? 3 : 0}
                            onClick={() => handleChartSliceClick(entry.name)}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, "Devices"]} />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="overflow-x-auto">
                  <p className="mb-3 text-sm text-slate-500">
                    {chartFilterHint(chartStatusFilter)}
                  </p>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        {["SERIAL NO.", "MODEL", "CURRENT FW", "REQUIRED FW", "COUNTRY", "COMPLIANCE", "LAST OTA"].map((h) => (
                          <th key={h} className="text-left text-[11px] font-semibold tracking-wider text-slate-400 pb-2 pr-4">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {filteredChartDevices.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-sm text-slate-500">No devices found for selected filters.</td>
                        </tr>
                      ) : (
                        filteredChartDevices.map((d) => (
                          <tr key={`chart-${d.id}`} className="hover:bg-slate-50 transition-colors">
                            <td className="py-3 pr-4 font-mono font-semibold text-sm text-slate-800">{d.serial}</td>
                            <td className="py-3 pr-4 text-sm text-teal-600">{d.model}</td>
                            <td className="py-3 pr-4 font-mono text-sm text-teal-600">{d.currentFw}</td>
                            <td className="py-3 pr-4 font-mono text-sm text-slate-600">{d.requiredFw}</td>
                            <td className="py-3 pr-4 text-sm text-slate-600">{d.country || "—"}</td>
                            <td className="py-3 pr-4"><ComplianceCell device={d} /></td>
                            <td className="py-3 pr-4 text-sm text-slate-500">{formatLastOta(d.lastUpdated)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="py-10 text-center text-sm text-slate-500">No data available for chart view.</p>
            ))}
          </div>
  );
}